import { useContext } from 'react';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useGlobalSearch from '../../API/hooks/search/useGlobalSearch';
import { queryFilters } from '../../utils/queryFilters';
import ItemsListSkeleton from '../../components/common/Skeleton/ItemsListSkeleton';
import DriversItem from '../Drivers/DriversItem/DriversItem';
import VehiclesItem from '../Vehicles/VehiclesItem/VehiclesItem';
import DevicesItem from '../Devices/DevicesItem/DevicesItem';
import AlertsItem from '../Alerts/AlertsItem/AlertsItem';
import WrapperContext from '../../components/layout/PageWrapper/context';
import S from './SearchPageStyles';
import SearchItem from './SearchItem';
import { Driver } from '../../API/types/Drivers';
import { Vehicle } from '../../API/types/Vehicles';
import { Device } from '../../API/types/Devices';
import { AlertDetails } from '../../API/types/Alerts';
import { Video } from '../../API/types/Videos';
import { alertEventsMap } from '../Alerts/constants/alertEventsMap';
import { User } from '../../API/types/Users';

const isEmailSearch = (text: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);

function SearchPage(): JSX.Element {
  const fleetId = useGetFleetId();
  const { searchInput } = useContext(WrapperContext);

  const { data, isLoading, isFetching } = useGlobalSearch(
    fleetId,
    `?input=${searchInput}&limitPerCollection=100`,
    searchInput,
  );

  const isEmail = isEmailSearch(searchInput);
  const normalizedInput = searchInput.trim().toLowerCase();

  const users = isEmail
    ? data?.users?.filter(
      (user: User) => user.email?.toLowerCase() === normalizedInput,
    )
    : data?.users;

  const drivers = isEmail
    ? data?.drivers?.filter(
      (driver: Driver) => driver.email?.toLowerCase() === normalizedInput,
    )
    : data?.drivers;

  const isEmpty = !drivers?.length && !data?.vehicles?.length && !data?.devices?.length && !users?.length && !data?.alerts?.length;

  return (
    <S.Wrapper>
      {isFetching && <ItemsListSkeleton />}
      {!isLoading && (
        <ul className="list-container">
          {!!drivers?.length && drivers?.map((item: Driver) => (
            <SearchItem
              title={`${item.firstName} ${item.lastName}`}
              subtitle={`${item.licenseName}${item.email ? `  |  ${item.email}` : ''}${item.phoneNumber ? `  |  ${item.phoneNumber}` : ''}${item.lastName ? `  |  ${item.lastName}` : ''}`}
              id={item._id}
              entity="driver"
            />
            // <DriversItem key={item._id} item={item} lightVersion />
          ))}
          {!!data?.vehicles?.length
            && data.vehicles?.map((item: Vehicle) => (
              <SearchItem
                title={item.licensePlateNumber}
                subtitle={`${item.manufacturer} ${item.model}${item.color ? `  |  ${item.color}` : ''}`}
                id={item._id}
                entity="vehicle"
              />
              // <VehiclesItem key={item._id} item={item} lightVersion />
            ))}
          {!!data?.devices?.length
            && data.devices?.map((item: Device) => (
              <SearchItem title={item.deviceId} subtitle={item.hwModel} id={item._id} entity="device" />
              // <DevicesItem key={item._id} item={item} lightVersion />
            ))}
          {!!data?.alerts?.length
            && data.alerts?.map((item: AlertDetails) => {
              const { shortDesc, fullDesc } = alertEventsMap[item.name as string];
              return (
                <SearchItem title={shortDesc} subtitle={fullDesc} id={item._id} entity="alert" alert={item} />
                // <AlertsItem key={item._id} item={item} />
              );
            })}
          {!!users?.length
            && users?.map((item: User) => (
              <SearchItem title={`${item.name}`} subtitle={item.email} id={item._id} entity="user" />
            ))}
          {!isFetching && isEmpty && <div className="no-data">No data found</div>}
        </ul>
      )}
    </S.Wrapper>
  );
}

export default SearchPage;
